import React, { useImperativeHandle, useState } from "react";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import AdministerMedicationFormTemplate from "../templates/AdministerMedicationFormTemplate";
import SkippedMedicationFormTemplate from "../templates/SkippedMedicationFormTemplate";

type MedicationAction = "administer" | "selfAdminister" | "skipped";

export interface MedicationActionModalRef {
  show: (action?: MedicationAction, medicationId?: number) => void;
}

const MedicationActionModal = React.forwardRef<MedicationActionModalRef>(
  (_, ref) => {
    const [showing, setShowing] = useState(false);
    const [action, setAction] = useState<MedicationAction | null>(null);
    const [medicationIdState, setMedicationIdState] = useState<number | null>(
      null,
    );

    useImperativeHandle(ref, () => ({
      show: (action = "administer", medicationId) => {
        setAction(action);
        setMedicationIdState(medicationId ?? null);
        setShowing(true);
      },
    }));

    const handleClose = () => {
      setShowing(false);
      setAction(null);
    };

    const renderForm = () => {
      if (medicationIdState === null) return null;

      switch (action) {
        case "selfAdminister":
          return (
            <AdministerMedicationFormTemplate
              onClose={handleClose}
              selfAdminister
              medicationId={medicationIdState}
            />
          );
        case "skipped":
          return (
            <SkippedMedicationFormTemplate
              onClose={handleClose}
              medicationId={medicationIdState}
            />
          );
        default:
          return (
            <AdministerMedicationFormTemplate
              onClose={handleClose}
              medicationId={medicationIdState}
            />
          );
      }
    };

    const title =
      action === "selfAdminister"
        ? "Self Administer Medication"
        : action === "skipped"
          ? "Skipped Medication"
          : "Administer Medication";

    return (
      <ReusableModal
        isVisible={showing}
        onClose={handleClose}
        className="w-full max-w-[984px] mx-auto"
        title={title}
        hasCancelButton
      >
        {renderForm()}
      </ReusableModal>
    );
  },
);

MedicationActionModal.displayName = "MedicationActionModal";
export { MedicationActionModal };
